/**
 * Process-wide "a native face capture is in flight" marker, shared by every
 * caller of FaceCaptureModule (the LOGIN / DELIVERIES auto-capture controller
 * and the KYC selfie tile).
 *
 * The native module accepts one launch at a time. The controller used to keep
 * its in-flight guard in a per-instance ref, so when the React tree was
 * remounted while a capture was pending (a recreated host activity on cold
 * start) the new controller instance launched again and was refused with
 * E_ALREADY_RUNNING. The pending promise and its callbacks live in the JS
 * context, not in the component, so the right behaviour for a new instance is
 * to leave the in-flight launch alone: its result still reaches Redux.
 *
 * Each launch is tagged with its owner so a caller can tell its own capture
 * (which it may push updates / a dismiss into) from someone else's (which it
 * must only wait for).
 */
export type FaceCaptureOwner = "auto" | "kyc";

interface InFlightLaunch {
  owner: FaceCaptureOwner;
  /** Resolves (never rejects) once the launch settles either way. */
  settled: Promise<void>;
}

let inFlight: InFlightLaunch | null = null;

/**
 * True while a launch started through {@link trackFaceCaptureLaunch} has not
 * settled — any owner's, or only `owner`'s when given.
 */
export const isFaceCaptureInFlight = (owner?: FaceCaptureOwner): boolean =>
  inFlight !== null && (owner === undefined || inFlight.owner === owner);

/**
 * Resolves when the launch in flight right now settles; `null` when nothing is
 * in flight. Lets a caller that had to stand aside try again afterwards.
 */
export const faceCaptureSettled = (): Promise<void> | null =>
  inFlight?.settled ?? null;

/**
 * Register a launch promise as the in-flight capture until it settles.
 * Returns the same promise so callers can `await` it directly.
 */
export const trackFaceCaptureLaunch = <T>(
  launch: Promise<T>,
  owner: FaceCaptureOwner,
): Promise<T> => {
  const entry: InFlightLaunch = {
    owner,
    settled: launch.then(
      () => undefined,
      () => undefined,
    ),
  };
  inFlight = entry;
  entry.settled.then(() => {
    if (inFlight === entry) inFlight = null;
  });
  return launch;
};

/** Test-only reset. */
export const __resetFaceCaptureInFlightForTests = (): void => {
  inFlight = null;
};
